import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, Link, Chip, alpha } from '@mui/material';
import { PublicRounded, OpenInNewRounded, SearchRounded } from '@mui/icons-material';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import AndieGenericToolRender from './AndieGenericToolRender';

type SearchResult = {
    title?: string;
    url?: string;
    description?: string;
    content?: string;
};
type ActionResponse = SearchResult[];
type ActionArgs = { query?: string; hl?: string; gl?: string; site?: string };

export type AndieWebSearchProps = ActionRenderAi<ActionResponse, ActionArgs>;

const MAX_VISIBLE = 5;

const domainOf = (url?: string): string => {
    if (!url) return '';
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url.replace(/^https?:\/\//, '').split('/')[0];
    }
};

// Convert an ISO 3166-1 alpha-2 country code (e.g. "br") into its flag emoji.
const flagOf = (code?: string): string => {
    const cc = code?.trim().toUpperCase() ?? '';
    if (!/^[A-Z]{2}$/.test(cc)) return '';
    return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

// Stable module-level component (NOT an inline render prop) so AndieGenericToolRender updates this
// subtree instead of remounting it on every streamed re-render. Renders only once complete.
const WebSearchResults: React.FC<ActionRenderAi<ActionResponse, ActionArgs>> = ({ status, args, result }) => {
    const { t } = useTranslation();
    if (status !== ActionResponseStatusAi.COMPLETE) return null;
    const results = Array.isArray(result) ? result : [];
    if (results.length === 0) return null;

    const visible = results.slice(0, MAX_VISIBLE);
    const extra = results.length - visible.length;

    const query = args?.query?.trim();
    const flag = flagOf(args?.gl);

    return (
        <Stack gap={1.25} sx={{ py: 0.5, pr: 0.5 }}>
            {(query || flag) && (
                <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ alignItems: 'center' }}>
                    {query && (
                        <Chip
                            size="small"
                            icon={<SearchRounded sx={{ fontSize: 14 }} />}
                            label={query}
                            sx={{
                                height: 'auto',
                                minHeight: 24,
                                maxWidth: '100%',
                                py: 0.25,
                                bgcolor: (theme) => alpha(theme.palette.andie.main, 0.1),
                                color: 'andie.main',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: 'andie.main', ml: 0.5 },
                                '& .MuiChip-label': {
                                    px: 0.75,
                                    whiteSpace: 'normal',
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                },
                            }}
                        />
                    )}
                    {flag && (
                        <Chip
                            size="small"
                            label={`${flag} ${args?.gl?.toUpperCase()}`}
                            sx={{
                                height: 24,
                                bgcolor: 'action.hover',
                                color: 'text.secondary',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 0.75 },
                            }}
                        />
                    )}
                </Stack>
            )}
            {visible.map((item, index) => {
                const host = domainOf(item.url);
                return (
                    <Stack key={`${item.url ?? index}`} gap={0.25} sx={{ minWidth: 0 }}>
                        <Link
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                                color: 'andie.main',
                            }}
                        >
                            <Typography
                                component="span"
                                variant="caption"
                                fontWeight={600}
                                noWrap
                                sx={{ color: 'inherit' }}
                            >
                                {item.title || host || item.url}
                            </Typography>
                            <OpenInNewRounded sx={{ fontSize: 12, flexShrink: 0 }} />
                        </Link>
                        {host && (
                            <Typography variant="caption" color="text.disabled" noWrap>
                                {host}
                            </Typography>
                        )}
                        {item.description && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {item.description}
                            </Typography>
                        )}
                    </Stack>
                );
            })}
            {extra > 0 && (
                <Typography variant="caption" color="text.disabled">
                    {t('andie.tool.web-search.more', { value: extra })}
                </Typography>
            )}
        </Stack>
    );
};

const AndieWebSearch = memo((props: AndieWebSearchProps) => {
    const { t } = useTranslation();

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.web-search.title')}
            icon={<PublicRounded fontSize="small" />}
            loadingMessages={(args) => [t('andie.tool.web-search.loading', { value: args?.query ?? '' })]}
            completeMessage={({ result }) =>
                t('andie.tool.web-search.complete', {
                    value: Array.isArray(result) ? result.length : 0,
                })
            }
            additionalComponent={WebSearchResults}
            autoExpandOnComplete={false}
            {...props}
        />
    );
});

AndieWebSearch.displayName = 'AndieWebSearch';

export default AndieWebSearch;
